<!-- frontend/src/components/Map/FloatingVideoStream.vue -->
<template>
  <div 
    v-if="isVisible && currentCamera"
    class="floating-video-stream fixed shadow-2xl overflow-hidden transition-all duration-300 w-[450px]"
    :class="[
      isDragging ? 'cursor-grabbing' : 'cursor-grab',
      isDarkMode ? 'dark' : 'light'
    ]"
    :style="{
      top: position.y + 'px',
      right: position.x + 'px',
      opacity: isDragging ? 0.9 : 1,
      transition: isDragging ? 'none' : 'all 0.3s ease',
      zIndex: 900
    }"
    @mousedown="startDrag"
  >
    <!-- Header - LIGHT MODE SESUAI HEADER/SIDEBAR -->
    <div 
      class="px-1 flex items-center justify-between cursor-move"
      style="height: 22px;"
      :class="isDarkMode ? 'bg-gradient-to-r from-gray-800 to-gray-900 text-white' : 'bg-white text-gray-800 border-b border-gray-200'"
      @mousedown="startDrag"
    >
      <div class="flex items-center justify-between w-full">
        <div class="flex items-center space-x-1 flex-1 min-w-0">
          <i class="fas fa-video text-blue-500 text-[9px]"></i>
          <span class="text-[9px] font-medium truncate">{{ currentCamera.name || currentCamera.camera_name || 'Camera' }}</span>
        </div>
        
        <div class="flex items-center space-x-0.5 flex-shrink-0">         
          <!-- Auto Rotate Button -->
          <button 
            @click.stop="toggleAutoRotate"
            class="rounded transition-colors flex items-center justify-center"
            style="width: 16px; height: 16px;"
            :class="[
              autoRotateEnabled 
                ? (isDarkMode ? 'bg-blue-600/80 text-white' : 'bg-blue-500 text-white')
                : (isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500')
            ]"
            :title="autoRotateEnabled ? 'Auto Rotate ON' : 'Auto Rotate OFF'"
          >
            <i class="fas fa-sync-alt text-[7px]" :class="{ 'fa-spin': autoRotateEnabled }"></i>
          </button>
          
          <!-- Fullscreen Button -->
          <button 
            @click.stop="toggleFullscreen"
            class="rounded transition-colors flex items-center justify-center"
            style="width: 16px; height: 16px;"
            :class="isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'"
            title="Fullscreen"
          >
            <i class="fas fa-expand text-[7px]"></i>
          </button>
        </div>
      </div>
    </div>
    
    <!-- IFRAME Content -->
    <div class="relative bg-black">
      <iframe
        ref="iframeElement"
        :src="currentCamera.url"
        class="w-full"
        style="aspect-ratio: 16 / 9;"
        allow="autoplay; encrypted-media; fullscreen; camera; microphone; display-capture"
        frameborder="0"
        allowfullscreen
        sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-modals allow-presentation"
        referrerpolicy="no-referrer-when-downgrade"
        @load="handleIframeLoad"
        @error="handleIframeError"
      ></iframe>
      
      <!-- Loading overlay -->
      <div 
        v-if="isLoading"
        class="absolute inset-0 flex items-center justify-center"
        :class="isDarkMode ? 'bg-black/70' : 'bg-black/50'"
      >
        <div class="spinner-stream" :class="isDarkMode ? 'border-gray-600 border-t-blue-400' : 'border-gray-300 border-t-blue-500'"></div>
        <span class="text-white text-[10px] ml-2">Loading...</span>
      </div>
      
      <!-- Error overlay -->
      <div 
        v-if="hasError"
        class="absolute inset-0 flex flex-col items-center justify-center"
        :class="isDarkMode ? 'bg-gray-900/90' : 'bg-black/80'"
      >
        <i class="fas fa-exclamation-triangle text-red-500 text-2xl mb-1"></i>
        <p class="text-white text-[10px] text-center px-4">Failed to load</p>
        <button 
          @click="reloadStream"
          class="mt-1.5 px-2 py-0.5 bg-blue-600 rounded text-[9px] hover:bg-blue-700 transition-colors text-white"
        >
          Retry
        </button>
      </div>
      
      <!-- Info overlay - muncul saat hover -->
      <div 
        class="absolute bottom-0 left-0 right-0 p-1 opacity-0 hover:opacity-100 transition-opacity"
        :class="isDarkMode ? 'bg-gradient-to-t from-gray-900/80 to-transparent' : 'bg-gradient-to-t from-black/60 to-transparent'"
      >
        <div class="flex items-center justify-between text-white text-[8px]">
          <span>
            <i class="fas fa-circle text-green-500 text-[4px] mr-1"></i>
            {{ streamStatus }}
          </span>
          <span v-if="autoRotateEnabled" class="text-blue-300">
            <i class="fas fa-sync-alt fa-spin mr-1 text-[8px]"></i>
            Auto
          </span>
          <span :class="isDarkMode ? 'text-purple-300' : 'text-purple-400'">
            Stream
          </span>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useThemeStore } from '../../stores/theme'

const props = defineProps({
  camera: {
    type: Object,
    default: null
  },
  defaultPosition: {
    type: Object,
    default: () => ({ x: 20, y: 80 })
  },
  autoShow: {
    type: Boolean,
    default: false
  },
  autoRotate: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['close', 'camera-change', 'auto-rotate-change'])

// Theme store
const themeStore = useThemeStore()
const isDarkMode = computed(() => themeStore.isDarkMode)

// State
const isVisible = ref(false)
const currentCamera = ref(null)
const position = ref({ ...props.defaultPosition })
const isDragging = ref(false)
const dragStart = ref({ x: 0, y: 0, startX: 0, startY: 0 })
const isLoading = ref(false)
const hasError = ref(false)
const iframeElement = ref(null)
const autoRotateEnabled = ref(props.autoRotate)
let autoRotateTimer = null
let retryCount = 0
const MAX_RETRIES = 3

const streamStatus = computed(() => {
  if (hasError.value) return 'Error'
  if (isLoading.value) return 'Loading...'
  return 'Live'
})

function startDrag(e) {
  if (e.target.closest('button')) return
  
  e.preventDefault()
  isDragging.value = true
  
  const rect = e.currentTarget.getBoundingClientRect()
  dragStart.value = {
    x: e.clientX - rect.left,
    y: e.clientY - rect.top,
    startX: position.value.x,
    startY: position.value.y
  }
  
  document.addEventListener('mousemove', onDrag)
  document.addEventListener('mouseup', stopDrag)
}

function onDrag(e) {
  if (!isDragging.value) return
  
  e.preventDefault()
  
  let newX = dragStart.value.startX - (e.clientX - dragStart.value.x)
  let newY = dragStart.value.startY - (e.clientY - dragStart.value.y)
  
  const streamElement = document.querySelector('.floating-video-stream')
  if (streamElement) {
    const rect = streamElement.getBoundingClientRect()
    newX = Math.max(0, Math.min(window.innerWidth - rect.width, newX))
    newY = Math.max(0, Math.min(window.innerHeight - rect.height, newY))
  }
  
  position.value = { x: newX, y: newY }
}

function stopDrag() {
  isDragging.value = false
  document.removeEventListener('mousemove', onDrag)
  document.removeEventListener('mouseup', stopDrag)
  localStorage.setItem('floating_video_position', JSON.stringify(position.value))
}

function toggleFullscreen() {
  if (!iframeElement.value) return
  
  if (document.fullscreenElement) {
    document.exitFullscreen()
  } else {
    iframeElement.value.requestFullscreen()
  }
}

function toggleAutoRotate() {
  autoRotateEnabled.value = !autoRotateEnabled.value
  emit('auto-rotate-change', autoRotateEnabled.value)
  localStorage.setItem('floating_video_auto_rotate', autoRotateEnabled.value)
  
  if (autoRotateEnabled.value) {
    startAutoRotateTimer()
  } else {
    clearAutoRotateTimer()
  }
}

function clearAutoRotateTimer() {
  if (autoRotateTimer) {
    clearInterval(autoRotateTimer)
    autoRotateTimer = null
  }
}

function startAutoRotateTimer() {
  clearAutoRotateTimer()
  if (autoRotateEnabled.value && currentCamera.value) {
    autoRotateTimer = setInterval(() => {
      if (isVisible.value) {
        emit('camera-change', currentCamera.value, true)
      }
    }, 15000)
  }
}

function closeStream() {
  clearAutoRotateTimer()
  
  if (iframeElement.value) {
    iframeElement.value.src = 'about:blank'
  }
  
  isVisible.value = false
  hasError.value = false
  isLoading.value = false
  currentCamera.value = null
  retryCount = 0
  
  emit('close')
}

function reloadStream() {
  if (!currentCamera.value || !currentCamera.value.url) return
  
  retryCount++
  hasError.value = false
  isLoading.value = true
  
  if (iframeElement.value) {
    const url = currentCamera.value.url
    iframeElement.value.src = url
  }
}

function handleIframeLoad() {
  isLoading.value = false
  hasError.value = false
  retryCount = 0
}

function handleIframeError() {
  console.error('❌ Iframe failed to load')
  
  if (retryCount < MAX_RETRIES) {
    setTimeout(() => {
      reloadStream()
    }, 2000)
  } else {
    isLoading.value = false
    hasError.value = true
  }
}

async function showStream(camera) {
  if (!camera || !camera.url) {
    console.warn('Invalid camera or missing URL:', camera)
    return
  }
  
  hasError.value = false
  isLoading.value = true
  retryCount = 0
  currentCamera.value = camera
  isVisible.value = true
  
  emit('camera-change', camera)
  
  if (autoRotateEnabled.value) {
    startAutoRotateTimer()
  }
  
  await nextTick()
  
  if (iframeElement.value) {
    iframeElement.value.src = camera.url
  }
}

function hideStream() {
  closeStream()
}

function isStreamVisible() {
  return isVisible.value
}

function loadSavedState() {
  const savedPosition = localStorage.getItem('floating_video_position')
  if (savedPosition) {
    try {
      const parsed = JSON.parse(savedPosition)
      if (parsed && typeof parsed.x === 'number' && typeof parsed.y === 'number') {
        position.value = parsed
      }
    } catch(e) {}
  }
  
  const savedAutoRotate = localStorage.getItem('floating_video_auto_rotate')
  if (savedAutoRotate !== null) {
    autoRotateEnabled.value = savedAutoRotate === 'true'
  }
}

function resetAllState() {
  clearAutoRotateTimer()
  
  if (iframeElement.value) {
    iframeElement.value.src = 'about:blank'
  }
  
  isVisible.value = false
  currentCamera.value = null
  isLoading.value = false
  hasError.value = false
  retryCount = 0
}

// Watch untuk perubahan tema
watch(isDarkMode, (dark) => {
  console.log('Floating video stream theme changed:', dark ? 'dark' : 'light')
})

watch(autoRotateEnabled, (enabled) => {
  if (enabled && isVisible.value && currentCamera.value) {
    startAutoRotateTimer()
  } else {
    clearAutoRotateTimer()
  }
})

watch(() => props.camera, (newCamera) => {
  if (newCamera && newCamera.url && props.autoShow) {
    showStream(newCamera)
  }
}, { immediate: true, deep: true })

onMounted(() => {
  loadSavedState()
  
  const handleResize = () => {
    const streamElement = document.querySelector('.floating-video-stream')
    if (streamElement && isVisible.value) {
      const rect = streamElement.getBoundingClientRect()
      position.value.x = Math.min(position.value.x, window.innerWidth - rect.width)
      position.value.y = Math.min(position.value.y, window.innerHeight - rect.height)
      position.value.x = Math.max(0, position.value.x)
      position.value.y = Math.max(0, position.value.y)
    }
  }
  
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  resetAllState()
})

defineExpose({
  showStream,
  hideStream,
  resetAllState,
  isVisible: isStreamVisible,
  currentCamera: () => currentCamera.value,
  toggleAutoRotate,
  autoRotateEnabled: () => autoRotateEnabled.value
})
</script>

<style scoped>
.floating-video-stream {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  user-select: none;
  backdrop-filter: blur(0px);
  background: #000;
  border-radius: 4px !important;
}

/* Pastikan child elements juga mengikuti radius */
.floating-video-stream > div:first-child {
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
}

.floating-video-stream > div:last-child {
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
}

/* Light mode border dan background */
.floating-video-stream.light {
  border: 1px solid #e5e7eb;
}

.floating-video-stream.light > div:first-child {
  background-color: #ffffff;
}

/* Dark mode border */
.floating-video-stream.dark {
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.floating-video-stream:active {
  cursor: grabbing;
}

.spinner-stream {
  width: 20px;
  height: 20px;
  border-width: 2px;
  border-style: solid;
  border-radius: 50%;
  animation: spin-stream 0.8s linear infinite;
}

@keyframes spin-stream {
  to { transform: rotate(360deg); }
}

iframe {
  background: #000;
  border: none;
  display: block;
}

.floating-video-stream:hover .opacity-0 {
  opacity: 1;
}

.cursor-grab:active {
  cursor: grabbing;
}
</style>